<template>
  <div>
    <!-- Search Bar -->
    <div class="bg-white rounded-lg shadow-sm p-4 w-full mb-4">
      <div class="flex flex-wrap items-center justify-between gap-2 p-4">
        <div class="w-64">
          <SearchBarComponent
            :data="services"
            search-key="name"
            @filtered="handleSearchFilter"
          />
        </div>
        <div class="ml-auto">
          <Button
            icon="pi pi-plus"
            label="Add Service"
            class="p-button-sm"
            severity="secondary"
            raised
            @click="openAddModal"
          />
        </div>
      </div>
    </div>

    <!-- Alphabet Filter -->
    <div class="bg-white rounded-lg shadow-sm p-4 w-full mb-4">
      <AlphabetFilterComponent
        :data="services"
        filter-key="name"
        @filtered="handleLetterFilter"
      />
    </div>

    <!-- Services Card Layout -->
    <div class="bg-white rounded-lg shadow-sm p-4 w-full">
      <!-- Empty state message when no services match filters -->
      <div
        v-if="filteredServices.length === 0"
        class="text-center py-8"
      >
        <i class="pi pi-cog text-5xl text-gray-300 mb-3" />
        <p class="text-gray-500">
          No services match your criteria
        </p>
      </div>

      <!-- Paginator above cards -->
      <Paginator
        v-if="filteredServices.length > 0"
        v-model:first="first"
        :rows="rows"
        :total-records="filteredServices.length"
        :rows-per-page-options="[6, 12, 24, 48]"
        class="mb-4"
        @page="onPageChange"
      />

      <!-- Card Layout -->
      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <div
          v-for="service in paginatedServices"
          :key="service.id"
          class="bg-white border border-gray-200 rounded-lg shadow-sm hover:shadow-md transition-shadow duration-300 overflow-hidden cursor-pointer"
          @click="viewService(service.id)"
        >
          <!-- Service Header with Logo -->
          <div class="h-40 relative flex items-center justify-center bg-purple-600">
            <!-- Logo image or placeholder -->
            <div
              class="w-24 h-24 rounded-full overflow-hidden border-4 border-white flex items-center justify-center"
            >
              <img
                v-if="service.image"
                :src="service.image"
                :alt="service.name"
                class="w-full h-full object-cover"
              >
              <div
                v-else
                class="w-full h-full flex items-center justify-center bg-gray-400"
              >
                <i class="pi pi-cog text-white text-4xl" />
              </div>
            </div>

            <!-- Company label if available -->
            <div
              v-if="service.company"
              class="absolute bottom-2 left-2 bg-white bg-opacity-90 px-3 py-1 rounded-lg text-sm font-medium"
            >
              {{ service.company }}
            </div>

            <!-- Bookable badge if applicable -->
            <div
              v-if="service.isBookable"
              class="absolute top-2 right-2 bg-green-100 bg-opacity-90 px-3 py-1 rounded-lg text-sm font-semibold text-green-800"
            >
              Bookable
            </div>
          </div>

          <!-- Service Details -->
          <div class="p-4">
            <h3 class="text-lg font-semibold text-gray-800 mb-2">
              {{ service.name }}
            </h3>

            <div class="flex items-center text-sm text-gray-600 mb-1">
              <i class="pi pi-envelope mr-2" />
              <span class="truncate">{{ service.email }}</span>
            </div>

            <div class="flex items-center text-sm text-gray-600 mb-1">
              <i class="pi pi-phone mr-2" />
              <span>{{ service.number }}</span>
            </div>

            <div
              v-if="service.website"
              class="flex items-center text-sm text-gray-600 mb-1"
            >
              <i class="pi pi-globe mr-2" />
              <span class="truncate">{{ service.website }}</span>
            </div>
          </div>

          <!-- Action Footer -->
          <div class="px-4 py-2 bg-gray-50 border-t border-gray-200 flex justify-between items-center">
            <div>
              <span
                v-if="service.vatRegistered"
                class="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full"
              >
                VAT Registered
              </span>
            </div>
            <Button
              icon="pi pi-eye"
              class="p-button-rounded p-button-text p-button-sm"
              @click.stop="viewService(service.id)"
            />
          </div>
        </div>
      </div>

      <!-- Paginator below cards -->
      <Paginator
        v-if="filteredServices.length > 0"
        v-model:first="first"
        :rows="rows"
        :total-records="filteredServices.length"
        :rows-per-page-options="[6, 12, 24, 48]"
        class="mt-4"
        @page="onPageChange"
      />
    </div>

    <!-- Reusable Dialog for Add Service -->
    <Dialog
      v-model:visible="isDialogVisible"
      :header="dialogHeader"
      :style="{ width: '50vw' }"
      :modal="true"
    >
      <ServiceModal
        :data="selectedService"
        :type="modalType"
        @add="handleDialogSubmit"
      />
    </Dialog>
  </div>
</template>

<script setup>
//Pinia Stores
import { useServiceStore } from '@/stores/servicesStore';
import { ref, computed, onMounted, watch } from 'vue';
import { useRouter } from 'vue-router';
import ServiceModal from '@/components/contacts/modals/ServiceModal.vue';
import SearchBarComponent from '@/components/shared/tableSearch/TableSearch.vue';
import AlphabetFilterComponent from '@/components/shared/alphabetFilter/AlphabetFilterComponent.vue';

// Initialize router for navigation
const router = useRouter();

// Initialize Pinia store and load services if not already loaded
const serviceStore = useServiceStore();
serviceStore.loadServices(); // Ensures data is loaded if not already
const services = computed(() => serviceStore.services); // Use computed for reactivity

// Pagination state
const first = ref(0);
const rows = ref(6);

// Dialog state
const isDialogVisible = ref(false);
const dialogHeader = ref('Service Details');
const modalType = ref('add');
const selectedService = ref({});

// Filtering state
const searchFilteredServices = ref([]);
const letterFilteredServices = ref([]);
const filteredServices = ref([]);

// Computed property for paginated services
const paginatedServices = computed(() => {
    const start = first.value;
    const end = Math.min(start + rows.value, filteredServices.value.length);
    return filteredServices.value.slice(start, end);
});

// Initialize filters when component mounts
onMounted(() => {
    // Initialize filtered services
    searchFilteredServices.value = [...services.value];
    letterFilteredServices.value = [...services.value];
    applyFilters();
});

// Watch for changes in services data
watch(services, (newServices) => {
    searchFilteredServices.value = [...newServices];
    letterFilteredServices.value = [...newServices];
    applyFilters();
});

// Apply combined filters
const applyFilters = () => {
    if (letterFilteredServices.value.length === services.value.length) {
        // If letter filter is not active, just use search results
        filteredServices.value = searchFilteredServices.value;
    } else if (searchFilteredServices.value.length === services.value.length) {
        // If search is not active, just use letter filter
        filteredServices.value = letterFilteredServices.value;
    } else {
        // Both filters active - find intersection
        filteredServices.value = searchFilteredServices.value.filter(service =>
            letterFilteredServices.value.some(s => s.id === service.id)
        );
    }
    // Reset pagination to first page when filters change
    first.value = 0;
};

// Handle filtered results from search
const handleSearchFilter = (filtered) => {
    searchFilteredServices.value = filtered;
    applyFilters();
};

// Handle filtered results from alphabet
const handleLetterFilter = (filtered) => {
    letterFilteredServices.value = filtered;
    applyFilters();
};

// Handle pagination page change
const onPageChange = (event) => {
    first.value = event.first;
    rows.value = event.rows;
};

// Navigate to view a specific service
const viewService = (id) => {
    router.push(`/view-service/${id}`);
};

// Open dialog to add a new service
const openAddModal = () => {
    selectedService.value = {};
    modalType.value = 'add';
    dialogHeader.value = 'Add Service';
    isDialogVisible.value = true;
};

// Handle dialog submit for both add and view
const handleDialogSubmit = (submittedService) => {
    if (modalType.value === 'add') {
        // Add the new service
        serviceStore.addService(submittedService);
    }
    isDialogVisible.value = false;
};
</script>

<style scoped>
.truncate {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 200px;
}
</style>